import { getToolName, isTextUIPart, isToolUIPart } from "ai";
import type { EstimateChatUIMessage } from "@/hooks/mutation/estimate-chat";

type AssistantPart = EstimateChatUIMessage["parts"][number];

type TurnBlock =
  | { kind: "text"; text: string }
  // successCount only counts finished, successful calls, so it never goes down as calls fail.
  // isDone is false while any call in the run is still in progress.
  | { kind: "tool"; toolName: string; successCount: number; isDone: boolean };

const isVisiblePart = (part: AssistantPart): boolean => (isTextUIPart(part) ? part.text.length > 0 : isToolUIPart(part));

/**
 * The turn's visible parts as display blocks. A run of calls to the same tool, with no text between
 * them, becomes one block, so five drafts show as one line rather than five. Failed calls stay in
 * their run without adding to its count: dropping them would make the count fall and lines vanish.
 */
export function toTurnBlocks(parts: AssistantPart[]): TurnBlock[] {
  const blocks: TurnBlock[] = [];
  for (const part of parts.filter(isVisiblePart)) {
    if (isTextUIPart(part)) {
      blocks.push({ kind: "text", text: part.text });
      continue;
    }
    if (!isToolUIPart(part)) continue;
    const toolName = getToolName(part);
    const succeeded = part.state === "output-available" ? 1 : 0;
    const isDone = part.state === "output-available" || part.state === "output-error";
    const last = blocks[blocks.length - 1];
    if (last?.kind === "tool" && last.toolName === toolName) {
      last.successCount += succeeded;
      last.isDone &&= isDone;
    } else {
      blocks.push({ kind: "tool", toolName, successCount: succeeded, isDone });
    }
  }
  return blocks;
}
